import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"

export type UsageDayStripProps = ComponentProps<"div"> & {
  /** One value per day, oldest first (weighted daily totals). */
  series: number[]
  /** Tooltip noun for the values ("5 active users"); default "activity". */
  unit?: string
}

// 28-block activity strip (uptime-history-bar style): one cell per day,
// intensity by share of the user's own busiest day. Native `title` tooltips
// instead of Radix so hundreds of rows stay cheap.
function UsageDayStrip(props: UsageDayStripProps) {
  const { series, unit = "activity", className, ...divProps } = props

  const max = Math.max(1, ...series)
  const today = new Date()

  return (
    <div
      className={cn("flex h-4 items-stretch gap-px", className)}
      role="img"
      aria-label={`${series.length}-day activity history`}
      {...divProps}
    >
      {series.map((value, i) => {
        const daysAgo = series.length - 1 - i
        const date = new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000)
        const label = `${date.toISOString().slice(0, 10)}: ${
          value > 0 ? `${value.toFixed(0)} ${unit}` : `no ${unit}`
        }`
        return (
          <div
            key={i}
            title={label}
            className={cn(
              // Green intensity ramp — same activity-calendar language as
              // the user panel's 90-day history strip (usage.css), distinct
              // from the amber UI chrome.
              "min-w-0 flex-1 rounded-[1px]",
              value <= 0 && "bg-muted",
              value > 0 && value <= max * 0.33 && "bg-green-500/30",
              value > max * 0.33 && value <= max * 0.66 && "bg-green-500/60",
              value > max * 0.66 && "bg-green-500",
            )}
          />
        )
      })}
    </div>
  )
}

export default UsageDayStrip
